import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { zodResolver } from '@hookform/resolvers/zod';
import { Briefcase, Check, Copy, Plus } from 'lucide-react';
import { useState } from 'react';
import { useForm } from 'react-hook-form';
import { toast } from 'sonner';
import { z } from 'zod';

import { Badge } from '@/components/ui/badge';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardHeader, CardTitle } from '@/components/ui/card';
import {
  Dialog,
  DialogContent,
  DialogFooter,
  DialogHeader,
  DialogTitle,
} from '@/components/ui/dialog';
import { Field, FieldGroup, FieldLabel } from '@/components/ui/field';
import { Input } from '@/components/ui/input';
import { Skeleton } from '@/components/ui/skeleton';
import {
  Table,
  TableBody,
  TableCell,
  TableHead,
  TableHeader,
  TableRow,
} from '@/components/ui/table';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { useEnterpriseContext } from '@/hooks/useEnterpriseContext';
import {
  createEnterpriseWorkerPass,
  getEnterpriseWorkerPasses,
  revokeEnterpriseWorkerPass,
  suspendEnterpriseWorkerPass,
} from '@/services/api.client';
import { formatDate, statusBadgeClass } from '@/lib/utils';

// ─── Form schema — phone-only requirement, validity is derived from a chip ───

const schema = z.object({
  workerName: z.string().min(1, 'Worker name is required'),
  workerPhone: z.string().optional(),
  windowStartTime: z.string().optional(),
  windowEndTime: z.string().optional(),
});

type FormValues = z.infer<typeof schema>;

type StatusTab = 'active' | 'suspended' | 'revoked';

// Validity chips. "Ongoing" = no end date. Custom days lets the admin pick a
// natural cadence without poking at a datetime input.
const PERIOD_CHIPS = [
  { id: 'month', label: '1 month', days: 30 },
  { id: 'quarter', label: '3 months', days: 90 },
  { id: 'half', label: '6 months', days: 180 },
  { id: 'year', label: '1 year', days: 365 },
  { id: 'ongoing', label: 'Ongoing', days: null },
] as const;
type PeriodId = (typeof PERIOD_CHIPS)[number]['id'];

type IssuedPass = {
  id: string;
  workerName: string;
  qrToken: string;
  validUntil: string | null;
};

// ─── Page ────────────────────────────────────────────────────────────────────

export function EnterpriseWorkerPassesPage() {
  const ctx = useEnterpriseContext();
  const qc = useQueryClient();
  const [open, setOpen] = useState(false);
  const [statusTab, setStatusTab] = useState<StatusTab>('active');
  const [period, setPeriod] = useState<PeriodId>('ongoing');
  const [issued, setIssued] = useState<IssuedPass | null>(null);
  const [selectedDays, setSelectedDays] = useState<string[]>(['MON', 'TUE', 'WED', 'THU', 'FRI']);

  const {
    data: passes,
    isLoading,
    isError,
  } = useQuery({
    queryKey: ['enterprise-worker-passes', ctx?.propertyId, ctx?.tenantId],
    queryFn: () => (ctx ? getEnterpriseWorkerPasses(ctx.propertyId, ctx.tenantId) : null),
    enabled: !!ctx,
  });

  const invalidate = () =>
    void qc.invalidateQueries({
      queryKey: ['enterprise-worker-passes', ctx?.propertyId, ctx?.tenantId],
    });

  const suspendMut = useMutation({
    mutationFn: (passId: string) =>
      ctx
        ? suspendEnterpriseWorkerPass(ctx.propertyId, ctx.tenantId, passId)
        : Promise.reject(new Error('Missing enterprise context')),
    onSuccess: invalidate,
    onError: () => toast.error('Failed to suspend pass'),
  });

  const revokeMut = useMutation({
    mutationFn: (passId: string) =>
      ctx
        ? revokeEnterpriseWorkerPass(ctx.propertyId, ctx.tenantId, passId)
        : Promise.reject(new Error('Missing enterprise context')),
    onSuccess: invalidate,
    onError: () => toast.error('Failed to revoke pass'),
  });

  const {
    register,
    handleSubmit,
    reset,
    formState: { errors, isSubmitting },
  } = useForm<FormValues>({ resolver: zodResolver(schema) });

  const handleOpenChange = (next: boolean) => {
    if (!next) {
      reset();
      setPeriod('ongoing');
      setSelectedDays(['MON', 'TUE', 'WED', 'THU', 'FRI']);
      setIssued(null);
    }
    setOpen(next);
  };

  const onSubmit = async (values: FormValues) => {
    if (!ctx) return;
    const chip = PERIOD_CHIPS.find((c) => c.id === period);
    const validUntil = chip?.days
      ? new Date(Date.now() + chip.days * 24 * 60 * 60 * 1000).toISOString()
      : undefined;
    try {
      const res = await createEnterpriseWorkerPass(ctx.propertyId, ctx.tenantId, {
        workerName: values.workerName,
        ...(values.workerPhone && { workerPhone: values.workerPhone }),
        ...(validUntil && { validUntil }),
        ...(selectedDays.length > 0 &&
          selectedDays.length < 7 && { workDays: selectedDays.join(',') }),
        ...(values.windowStartTime && { windowStartTime: values.windowStartTime }),
        ...(values.windowEndTime && { windowEndTime: values.windowEndTime }),
      });
      invalidate();
      setIssued({
        id: res.id,
        workerName: res.workerName,
        qrToken: res.qrToken ?? '',
        validUntil: res.validUntil,
      });
      reset();
    } catch {
      toast.error('Failed to issue pass — please try again');
    }
  };

  const issueAnother = () => {
    setIssued(null);
    setPeriod('ongoing');
  };

  const passLink = issued ? `${window.location.origin}/worker-pass/${issued.qrToken}` : '';

  const copyLink = () => {
    if (!issued) return;
    void navigator.clipboard.writeText(passLink);
    toast.success('Pass link copied — paste it into a message to the worker');
  };

  // Tab buckets
  const activeList = (passes ?? []).filter((p) => p.status === 'active');
  const suspendedList = (passes ?? []).filter((p) => p.status === 'suspended');
  const revokedList = (passes ?? []).filter((p) => p.status === 'revoked');
  const visiblePasses =
    statusTab === 'active' ? activeList : statusTab === 'suspended' ? suspendedList : revokedList;

  return (
    <div className="space-y-6 max-w-4xl">
      <div className="flex items-center justify-between">
        <div>
          <h1 className="text-2xl">Worker Passes</h1>
          <p className="text-sm text-muted-foreground mt-0.5">
            Persistent passes for cleaners, contractors, and ongoing vendors of your company
          </p>
        </div>
        <Button onClick={() => setOpen(true)}>
          <Plus className="size-4 mr-2" />
          Issue Worker Pass
        </Button>
      </div>

      <Tabs value={statusTab} onValueChange={(v) => setStatusTab(v as StatusTab)}>
        <TabsList>
          <TabsTrigger value="active">Active ({activeList.length})</TabsTrigger>
          <TabsTrigger value="suspended">Suspended ({suspendedList.length})</TabsTrigger>
          <TabsTrigger value="revoked">Revoked ({revokedList.length})</TabsTrigger>
        </TabsList>
        <TabsContent value="active" />
        <TabsContent value="suspended" />
        <TabsContent value="revoked" />
      </Tabs>

      <Card>
        <CardHeader className="pb-2">
          <CardTitle className="text-base capitalize">{statusTab} passes</CardTitle>
        </CardHeader>
        <CardContent>
          {isError ? (
            <div
              role="alert"
              className="rounded-xl border border-destructive/40 bg-destructive/5 p-4"
            >
              <p className="text-sm font-medium">Couldn&apos;t load worker passes</p>
              <p className="text-sm text-muted-foreground">Please try again later.</p>
            </div>
          ) : isLoading ? (
            <div className="space-y-3">
              {[1, 2, 3].map((i) => (
                <Skeleton key={i} className="h-10 w-full" />
              ))}
            </div>
          ) : visiblePasses.length ? (
            <Table>
              <TableHeader>
                <TableRow>
                  <TableHead>Worker</TableHead>
                  <TableHead>Valid From</TableHead>
                  <TableHead>Valid Until</TableHead>
                  <TableHead>Status</TableHead>
                  <TableHead className="text-right">Actions</TableHead>
                </TableRow>
              </TableHeader>
              <TableBody>
                {visiblePasses.map((pass) => (
                  <TableRow key={pass.id}>
                    <TableCell className="font-medium">{pass.workerName}</TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {formatDate(pass.validFrom)}
                    </TableCell>
                    <TableCell className="text-sm text-muted-foreground">
                      {pass.validUntil ? formatDate(pass.validUntil) : 'Ongoing'}
                    </TableCell>
                    <TableCell>
                      <Badge className={statusBadgeClass(pass.status)}>{pass.status}</Badge>
                    </TableCell>
                    <TableCell className="text-right space-x-2">
                      {pass.status === 'active' && (
                        <Button
                          variant="outline"
                          size="sm"
                          onClick={() => suspendMut.mutate(pass.id)}
                          disabled={
                            (suspendMut.isPending && suspendMut.variables === pass.id) ||
                            (revokeMut.isPending && revokeMut.variables === pass.id)
                          }
                        >
                          Suspend
                        </Button>
                      )}
                      {(pass.status === 'active' || pass.status === 'suspended') && (
                        <Button
                          variant="ghost"
                          size="sm"
                          className="text-destructive hover:text-destructive"
                          onClick={() => revokeMut.mutate(pass.id)}
                          disabled={
                            (suspendMut.isPending && suspendMut.variables === pass.id) ||
                            (revokeMut.isPending && revokeMut.variables === pass.id)
                          }
                        >
                          Revoke
                        </Button>
                      )}
                    </TableCell>
                  </TableRow>
                ))}
              </TableBody>
            </Table>
          ) : (
            <div className="py-10 text-center">
              <Briefcase className="size-8 text-muted-foreground/40 mx-auto mb-2" />
              <p className="text-sm text-muted-foreground">
                {statusTab === 'active' && 'No active worker passes.'}
                {statusTab === 'suspended' && 'No suspended passes.'}
                {statusTab === 'revoked' && 'No revoked passes.'}
              </p>
              {statusTab === 'active' && (
                <Button variant="link" size="sm" onClick={() => setOpen(true)} className="mt-1">
                  Issue your first pass
                </Button>
              )}
            </div>
          )}
        </CardContent>
      </Card>

      <Dialog open={open} onOpenChange={handleOpenChange}>
        <DialogContent aria-describedby={undefined} className="sm:max-w-md">
          {issued ? (
            // ── Post-submit confirmation ────────────────────────────────────
            <div className="space-y-4">
              <div className="flex items-center gap-2">
                <div className="size-9 rounded-full bg-[var(--status-green-tint)] flex items-center justify-center">
                  <Check className="size-5 text-[var(--status-green-text)]" />
                </div>
                <div>
                  <DialogTitle>Pass issued</DialogTitle>
                  <p className="text-sm text-muted-foreground">
                    {issued.workerName} can now use this pass at the gate.
                  </p>
                </div>
              </div>

              <div className="rounded-lg border border-border bg-muted/30 p-3 space-y-2">
                <div className="flex items-center justify-between gap-2">
                  <span className="text-xs uppercase tracking-wide text-muted-foreground">
                    Pass link for the worker
                  </span>
                  <Button variant="ghost" size="sm" onClick={copyLink}>
                    <Copy className="size-3.5 mr-1" />
                    Copy
                  </Button>
                </div>
                <p className="text-sm font-mono break-all">{passLink}</p>
                <p className="text-xs text-muted-foreground">
                  Share this with the worker — they open it on their phone and show the QR at the
                  gate. If a phone number was provided, Secure Labs texts it to them too.
                </p>
              </div>

              {issued.validUntil ? (
                <p className="text-xs text-muted-foreground">
                  Valid until <span className="font-medium">{formatDate(issued.validUntil)}</span>
                </p>
              ) : (
                <p className="text-xs text-muted-foreground">
                  This is an <span className="font-medium">ongoing</span> pass with no expiry.
                </p>
              )}

              <DialogFooter>
                <Button type="button" variant="outline" onClick={issueAnother}>
                  Issue another
                </Button>
                <Button type="button" onClick={() => handleOpenChange(false)}>
                  Done
                </Button>
              </DialogFooter>
            </div>
          ) : (
            // ── Issue form ─────────────────────────────────────────────────
            <>
              <DialogHeader>
                <DialogTitle>Issue Worker Pass</DialogTitle>
                <p className="text-sm text-muted-foreground">
                  Persistent pass for someone who comes to your company regularly.
                </p>
              </DialogHeader>
              <form onSubmit={(e) => void handleSubmit(onSubmit)(e)} noValidate>
                <FieldGroup>
                  <Field>
                    <FieldLabel htmlFor="workerName">Worker name</FieldLabel>
                    <Input
                      id="workerName"
                      type="text"
                      placeholder="e.g. Mary Wanjiru"
                      aria-invalid={!!errors.workerName}
                      {...register('workerName')}
                    />
                    {errors.workerName && (
                      <p className="text-sm text-destructive" role="alert">
                        {errors.workerName.message}
                      </p>
                    )}
                  </Field>
                  <Field>
                    <FieldLabel htmlFor="workerPhone">Phone (optional)</FieldLabel>
                    <Input
                      id="workerPhone"
                      type="tel"
                      placeholder="+254 700 000 000"
                      {...register('workerPhone')}
                    />
                    <p className="text-xs text-muted-foreground">
                      We&apos;ll text them the pass automatically if you add a number.
                    </p>
                  </Field>
                  <Field>
                    <FieldLabel>How long is this pass valid?</FieldLabel>
                    <div className="flex flex-wrap gap-2">
                      {PERIOD_CHIPS.map((c) => (
                        <button
                          key={c.id}
                          type="button"
                          onClick={() => setPeriod(c.id)}
                          className={
                            'rounded-full border px-3 py-1 text-xs font-medium transition-colors ' +
                            (period === c.id
                              ? 'border-primary bg-primary/15 text-primary'
                              : 'border-border bg-background hover:bg-muted')
                          }
                          aria-pressed={period === c.id}
                        >
                          {c.label}
                        </button>
                      ))}
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Pass starts immediately. &quot;Ongoing&quot; is the right choice for permanent
                      cleaners or vendors.
                    </p>
                  </Field>
                  <Field>
                    <FieldLabel>Work days</FieldLabel>
                    <div className="flex flex-wrap gap-1.5">
                      {(['MON', 'TUE', 'WED', 'THU', 'FRI', 'SAT', 'SUN'] as const).map((day) => (
                        <button
                          key={day}
                          type="button"
                          onClick={() =>
                            setSelectedDays((prev) =>
                              prev.includes(day) ? prev.filter((d) => d !== day) : [...prev, day],
                            )
                          }
                          className={
                            'rounded-md border px-2.5 py-1 text-xs font-medium transition-colors ' +
                            (selectedDays.includes(day)
                              ? 'border-primary bg-primary/15 text-primary'
                              : 'border-border bg-background hover:bg-muted')
                          }
                          aria-pressed={selectedDays.includes(day)}
                        >
                          {day.slice(0, 3)}
                        </button>
                      ))}
                    </div>
                    <p className="text-xs text-muted-foreground">
                      Select the days this worker comes in. All days = daily access.
                    </p>
                  </Field>
                  <Field>
                    <FieldLabel>Access hours</FieldLabel>
                    <div className="grid grid-cols-2 gap-3">
                      <Input
                        id="windowStartTime"
                        type="time"
                        defaultValue="08:00"
                        {...register('windowStartTime')}
                      />
                      <Input
                        id="windowEndTime"
                        type="time"
                        defaultValue="18:00"
                        {...register('windowEndTime')}
                      />
                    </div>
                    <p className="text-xs text-muted-foreground">
                      The worker can only enter between these hours. Defaults to 08:00–18:00.
                    </p>
                  </Field>
                </FieldGroup>
                <DialogFooter className="mt-4">
                  <Button type="button" variant="outline" onClick={() => handleOpenChange(false)}>
                    Cancel
                  </Button>
                  <Button type="submit" disabled={isSubmitting}>
                    {isSubmitting ? 'Issuing…' : 'Issue pass'}
                  </Button>
                </DialogFooter>
              </form>
            </>
          )}
        </DialogContent>
      </Dialog>
    </div>
  );
}
